import React, { useContext, useState, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import classnames from 'classnames';
import { getContext, useCombinedRefs } from 'common';
import useHover from 'common/useHover';
import { CONSTANTS } from 'common/ListKeyboardNav';
import DRAG_TYPES from 'common/DragTypes';
import TREE_CONSTANTS from 'components/Tree/constants';
import TreeDragLine from 'components/Tree/TreeDragLine';
import Tooltip from 'components/Tooltip/Tooltip';
import { TreeContext, TreeBranchContext, TreeErrorMessage } from './';

import Styled from './styles';

const {
  LIST_ITEM_TABABLE_ITEM,
  LIST_ITEM_SELECTED,
} = CONSTANTS;
const { TREE_NODE } = TREE_CONSTANTS;
const { TREEITEM } = DRAG_TYPES;


interface TreeNodeContextInterface {
  error?: boolean;
  errorMessage?: React.ReactNode;
  disabled?: boolean;
  selected?: boolean;
  isFocused?: boolean;
  isHovered?: boolean | ((node: any) => void);
  level?: number;
}

const TreeNodeContext = React.createContext<TreeNodeContextInterface | null>({});

export interface TreeNodeProps extends React.ComponentPropsWithRef<'div'> {
  /** Required id if Tree is using drag and drop. */
  id?: string;
  /** Display the list item as disabled. */
  disabled?: boolean;
  /** Display the list item as selected. */
  selected?: boolean;
  /** If true, place focus on this item. */
  focusItem?: boolean;
  /** If true, the base area will be rendered in the error state. */
  error?: boolean;
  /** If `error` property is true, the `errorMessage` will be displayed. `Typography` component can be used. */
  errorMessage?: React.ReactNode;
  /** children, displayed as one leaf item in the Tree. Consider using the TreeIcon, TreeText components and/or plain text. */
  children?: React.ReactNode;
  /** If true, allow the item text to be edited. */
  allowEdit?: boolean;
  /** Function to render the contextual menu for this `TreeNode` or a node. Consider using `TreeMoreButton` in this function or node. */
  renderContextMenu?: (...args: unknown[]) => unknown | React.ReactNode;
  /** Override level in the Tree. Usually inferred via context from the TreeBranch. */
  level?: number;
  /** Display an optional tooltip. See Tooltip component documentation for full list of properties. */
  tooltipProps?: {
    /** The content of the Tooltip. */
    tooltip: React.ReactNode,
    /** If `true`, disables tooltip from triggering. */
    disable?: boolean
  };
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/**
 * Terminal Tree item (leaf).  Children may be text, icon and other elements.
 * Any other props will be provided to the root element.
 */
const TreeNode = React.forwardRef<HTMLElement, TreeNodeProps>((props: TreeNodeProps, forwardedRef) => getContext(({ RTL }) => {
  const {
    children,
    disabled,
    selected,
    focusItem,
    error,
    errorMessage,
    onClick,
    onKeyDown,
    allowEdit,
    renderContextMenu,
    tooltipProps,
    level, // in cases of using react-virtualized-tree
    ...otherProps
  } = props;
  const treeContext = useContext(TreeContext);
  const treeBranchContext = useContext(TreeBranchContext);
  const [hoverRef, isHovered] = useHover();
  const [isFocused, setIsFocused] = useState(false);
  const { disable: tooltipPropsDisable, ...restOfTooltipProps } = tooltipProps;

  const allowDragAndDrop = treeContext.dnd;
  const [{ isDragging }, drag] = useDrag({
    item: { draggedId: props.id, type: TREEITEM },
    collect: monitor => ({
      isDragging: !!monitor.isDragging(),
    }),
  });
  const [{ isOverCurrent }, drop] = useDrop({
    accept: TREEITEM,
    drop(item, monitor) {
      const didDrop = monitor.didDrop();
      if (didDrop) {
        return;
      }
      // Next line is added as "draggedId" doesn't exist in dnd API.
      const { draggedId } = item as unknown as { draggedId: string };
      allowDragAndDrop && treeContext.dndCallback({ draggedId, droppedOnToId: props.id });
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true }),
      didDrop: monitor.didDrop(),
    }),
  });
  const ref = useCombinedRefs(forwardedRef, hoverRef, allowDragAndDrop && drag, allowDragAndDrop && drop);
  useEffect(() => {
    if (focusItem === true) {
      ref && ref.current && ref.current.focus();
    }
  }, [focusItem]);

  const handleClick = (event) => {
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    onClick && onClick(event);
  };

  const handleKeyDown = (event) => {
    if (disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    event.persist();
    onKeyDown && onKeyDown(event);
    // another event for Tree for Keyboard navigation of ARROW_LEFT
    treeContext && treeContext.arrowKeyCallback && treeContext.arrowKeyCallback(event);
  };

  const handleFocus = (event) => {
    const { onFocus } = props;
    onFocus && onFocus(event);
    setIsFocused(true);
  };

  const handleBlur = (event) => {
    const { onBlur } = props;
    onBlur && onBlur(event);
    setIsFocused(false);
  };

  const classes = classnames({
    [`${TREE_NODE}`]: true,
    [`${LIST_ITEM_TABABLE_ITEM}-${treeContext.uniqueTreeId}`]: !disabled,
    [LIST_ITEM_SELECTED]: !disabled && selected,
  });
  const hideContextMenu = !selected && !isFocused && !isHovered;
  const hasErrorMessage = error && errorMessage !== undefined;
  let treeLevel = treeBranchContext.level !== undefined ? treeBranchContext.level : 0;
  if (treeBranchContext.level === undefined) {
    treeLevel = level;
  }

  const content = (
    <>
      <Styled.TreeNodeWrapper>
        <Styled.TreeNode
          className={classes}
          aria-disabled={disabled}
          ref={ref}
          isDragging={allowDragAndDrop && isDragging}
          dnd={allowDragAndDrop}
          RTL={RTL}
          $theme={treeContext.theme}
          level={treeLevel}
          tabIndex={-1}
          disabled={disabled}
          selected={selected}
          aria-selected={selected}
          role="treeitem"
          allowEdit={allowEdit}
          onDoubleClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          {...otherProps}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          <TreeNodeContext.Provider value={{ selected, disabled, isFocused, isHovered, error, errorMessage, level: treeLevel }} >
            {children}
          </TreeNodeContext.Provider>
          {renderContextMenu &&
            <Styled.TreeContextMenu RTL={RTL} $theme={treeContext.theme} tabIndex={-1} hide={hideContextMenu}>
              {renderContextMenu && (typeof renderContextMenu === 'function') && renderContextMenu()}
              {renderContextMenu && (typeof renderContextMenu === 'object') && renderContextMenu}
            </Styled.TreeContextMenu>
          }
        </Styled.TreeNode>
        {hasErrorMessage &&
          <Styled.TreeErrorContainer $theme={treeContext.theme} RTL={RTL} level={treeLevel} error={error}>
            {typeof errorMessage === 'string' ? <TreeErrorMessage>{errorMessage}</TreeErrorMessage> : errorMessage}
          </Styled.TreeErrorContainer>
        }
      </Styled.TreeNodeWrapper>
      {allowDragAndDrop && isOverCurrent && <TreeDragLine />}
    </>
  );
  return tooltipPropsDisable ?
    content
    :
    (
      <Tooltip
        {...{
          placement: 'bottom',
          fallbackPlacements: ['top', 'left', 'right'],
          trigger: ['hover', 'focus'],
          modifiers: { offset: { offset: '0, -4' } },
          tooltip: '',
        }}
        {...restOfTooltipProps}
      >
        {content}
      </Tooltip>
    );
}));

TreeNode.displayName = 'TreeNode';

TreeNode.defaultProps = {
  id: undefined,
  disabled: false,
  selected: false,
  focusItem: false,
  error: false,
  errorMessage: undefined,
  children: undefined,
  allowEdit: false,
  renderContextMenu: undefined,
  level: undefined,
  tooltipProps: {
    disable: true,
    tooltip: '',
  },
  onClick: undefined,
  onKeyDown: undefined,
  onFocus: undefined,
  onBlur: undefined
};

export {
  TreeNode as default,
  TreeNodeContext,
};
